import React from 'react';

type WindowStatus = 'working' | 'idle' | 'live';

interface SxWindowProps {
  /** 等宽大写标题，模仿真实应用的窗口标题栏 */
  title: string;
  /** 右上角状态标注。working/live 会带呼吸指示点 */
  status?: WindowStatus;
  statusLabel?: string;
  children: React.ReactNode;
  className?: string;
  /** body 是否去掉内边距（图表/列表类内容需要贴边） */
  flush?: boolean;
}

const STATUS_TEXT: Record<WindowStatus, string> = {
  working: '运行中',
  live: '实时',
  idle: '待指令',
};

/**
 * 模拟应用窗口。
 *
 * 参考站的说服力不来自功能描述，而来自"展示功能正在运行的样子"——
 * 一个带状态指示的窗口壳，比一段文案更能让人相信产品是真实存在的。
 *
 * 精度要点：红绿灯刻意用中性灰而非 macOS 彩色（彩色会把注意力从内容上引开）、
 * 标题栏高度压到 38px、状态文字用等宽字体。
 */
const SxWindow: React.FC<SxWindowProps> = ({
  title,
  status,
  statusLabel,
  children,
  className = '',
  flush = false,
}) => (
  <div className={`sx-window ${className}`}>
    <div className="sx-window-bar">
      <div className="sx-window-lights" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
      <span className="sx-window-title truncate">{title}</span>
      {status && (
        <span className="ml-auto flex items-center gap-2">
          <span className={`sx-dot ${status === 'idle' ? '' : 'sx-dot-live'}`} aria-hidden="true" />
          <span className="sx-mono" style={{ letterSpacing: '0.06em' }}>
            {statusLabel ?? STATUS_TEXT[status]}
          </span>
        </span>
      )}
    </div>
    <div className={flush ? '' : 'sx-window-body'}>{children}</div>
  </div>
);

export default SxWindow;
